import { useEffect, useState, useCallback } from "react";
import { api, fileUrl } from "@/lib/api";
import { BadgeRow } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, FileText, ShieldCheck, Sparkles, TrendingUp, ChevronDown } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const CHART_MARGIN = { top: 5, right: 8, left: -18, bottom: 0 };
const fmtDay = (d) => d.slice(5);

export default function AdminConsole() {
  const [businesses, setBusinesses] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [revenue, setRevenue] = useState(null);

  const load = useCallback(async () => {
    setBusinesses(await api.get("/admin/vetting").then((r) => r.data).catch(() => []));
    setDisputes(await api.get("/admin/disputes").then((r) => r.data).catch(() => []));
    setRevenue(await api.get("/admin/revenue").then((r) => r.data).catch(() => null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = businesses.filter((b) => b.status === "pending");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-heading text-3xl font-extrabold text-slate-950 flex items-center gap-2"><ShieldCheck size={26} className="text-amber-600" /> Admin & Vetting Console</h1>
      <p className="mt-1 text-slate-500">Review documents, assign trust badges, and manage escrow disputes.</p>

      <Tabs defaultValue="vetting" className="mt-8">
        <TabsList>
          <TabsTrigger value="vetting" data-testid="admin-tab-vetting">Vetting Queue ({pending.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="admin-tab-all">All Businesses ({businesses.length})</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes">Escrow & Disputes ({disputes.length})</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="admin-tab-revenue">Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="vetting">
          {pending.length === 0 ? <Empty text="No businesses awaiting review." /> :
            <div className="space-y-5">{pending.map((b) => <VettingCard key={b.id} b={b} onChanged={load} />)}</div>}
        </TabsContent>
        <TabsContent value="all">
          <div className="space-y-5">{businesses.map((b) => <VettingCard key={b.id} b={b} onChanged={load} />)}</div>
        </TabsContent>
        <TabsContent value="disputes">
          {disputes.length === 0 ? <Empty text="No escrow transactions yet." /> :
            <div className="space-y-3">{disputes.map((d) => <DisputeRow key={d.session_id} d={d} onChanged={load} />)}</div>}
        </TabsContent>
        <TabsContent value="revenue">
          <RevenueTab revenue={revenue} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RevenueTab({ revenue }) {
  if (!revenue) return <Empty text="No revenue data yet." />;
  const cards = [
    { label: "Total revenue", value: money(revenue.total_revenue), testid: "rev-total", accent: "text-slate-950" },
    { label: "Featured placement", value: money(revenue.featured_total), testid: "rev-featured", accent: "text-amber-700" },
    { label: "Escrow commission earned (15%)", value: money(revenue.commission_earned), testid: "rev-commission", accent: "text-emerald-700" },
    { label: "Commission held in escrow", value: money(revenue.commission_pending), testid: "rev-commission-pending", accent: "text-slate-500" },
  ];
  return (
    <div className="space-y-6" data-testid="admin-revenue">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className={`font-heading text-3xl font-extrabold ${c.accent}`}>{c.value}</p>
            <p className="mt-1 text-xs font-medium text-slate-600">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 font-semibold px-3 py-1"><Sparkles size={13} /> {revenue.active_featured_count} businesses featured now</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 font-semibold px-3 py-1"><TrendingUp size={13} /> {revenue.active_subscriptions} active auto-renew subscriptions</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold px-3 py-1">Featured: {money(revenue.featured_onetime_total)} one-time + {money(revenue.featured_sub_total)} subscriptions</span>
      </div>

      {revenue.series && revenue.series.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="revenue-charts">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-heading font-bold text-slate-900 text-sm">Featured revenue · last 30 days</h3>
            <div className="mt-3 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue.series} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#d97706" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#d97706" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={fmtDay} interval={6} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={44} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [money(v), "Revenue"]} labelClassName="text-xs" />
                  <Area type="monotone" dataKey="revenue" stroke="#d97706" strokeWidth={2} fill="url(#revFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-heading font-bold text-slate-900 text-sm">Featured impressions · last 30 days</h3>
            <div className="mt-3 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue.series} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="impFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0f172a" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#0f172a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={fmtDay} interval={6} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={30} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, "Impressions"]} labelClassName="text-xs" />
                  <Area type="monotone" dataKey="impressions" stroke="#0f172a" strokeWidth={2} fill="url(#impFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="revenue-recent">
        <h3 className="font-heading font-bold text-slate-900">Recent featured purchases</h3>
        {(!revenue.recent || revenue.recent.length === 0) ? (
          <p className="mt-3 text-sm text-slate-400">No featured purchases yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {revenue.recent.map((r, i) => (
              <div key={`${r.business_name}-${r.date}-${i}`} data-testid={`revenue-row-${i}`} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div>
                  <p className="font-medium text-slate-800">{r.business_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{r.plan} · {r.kind}{r.date ? ` · ${new Date(r.date).toLocaleDateString()}` : ""}</p>
                </div>
                <p className="font-semibold text-slate-900">{money(r.amount)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">{text}</div>;

function VettingCard({ b, onChanged }) {
  const [badges, setBadges] = useState({ vetted: b.badges?.vetted, bonded: b.badges?.bonded, insured: b.badges?.insured });
  const [reason, setReason] = useState(b.reason || "");

  const reviewBiz = async (status) => {
    try { await api.post(`/admin/businesses/${b.id}/review`, { status, reason, ...badges }); toast.success(`Business ${status}`); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const reviewDoc = async (doc, status) => {
    const expiry = doc.doc_type === "insurance" ? (doc.expiry || prompt("Insurance expiry (YYYY-MM-DD)?") || null) : null;
    try { await api.post(`/admin/documents/${doc.id}/review`, { status, expiry }); toast.success(`Document ${status}`); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const docIcon = (s) => s === "approved" ? <CheckCircle2 size={14} className="text-emerald-600" /> : s === "rejected" ? <XCircle size={14} className="text-red-600" /> : <Clock size={14} className="text-amber-600" />;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`vetting-card-${b.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-lg text-slate-950">{b.business_name}</h3>
          <p className="text-sm text-slate-500">{b.owner_email} · {(b.trades || []).join(", ") || "no trades"} · {(b.service_areas || []).join(", ") || "no areas"}</p>
        </div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 capitalize">{b.status}</span>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {(b.documents || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              {docIcon(d.status)} <FileText size={14} className="text-slate-400" />
              <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="font-medium text-slate-700 hover:text-amber-600 capitalize">{d.doc_type.replace("_", " ")}</a>
            </div>
            <div className="flex gap-1">
              <button data-testid={`admin-doc-approve-${d.id}`} onClick={() => reviewDoc(d, "approved")} className="text-xs px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 font-semibold">Approve</button>
              <button data-testid={`admin-doc-reject-${d.id}`} onClick={() => reviewDoc(d, "rejected")} className="text-xs px-2 py-1 rounded-md bg-red-100 text-red-700 font-semibold">Reject</button>
            </div>
          </div>
        ))}
        {(!b.documents || b.documents.length === 0) && <p className="text-sm text-slate-400">No documents uploaded.</p>}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        {["vetted", "bonded", "insured"].map((k) => (
          <label key={k} className="flex items-center gap-2 text-sm font-medium capitalize cursor-pointer">
            <Checkbox data-testid={`admin-badge-${k}-${b.id}`} checked={badges[k]} onCheckedChange={(v) => setBadges((s) => ({ ...s, [k]: !!v }))} /> {k}
          </label>
        ))}
      </div>

      <Input className="mt-4 h-10" placeholder="Reason (for rejection)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`admin-reason-${b.id}`} />

      <div className="mt-4 flex gap-3">
        <Button data-testid={`admin-vetting-approve-button-${b.id}`} onClick={() => reviewBiz("approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Approve & assign badges</Button>
        <Button data-testid={`admin-vetting-reject-button-${b.id}`} onClick={() => reviewBiz("rejected")} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
      </div>
    </div>
  );
}

function EscrowDetails({ d }) {
  const status = d.status === "refunded" ? "Refunded" : d.released ? "Released" : "Held in escrow";
  const rows = [
    ["Job value", money(d.job_amount), "job-value"],
    ["Client service fee (3%)", money(d.service_fee), "service-fee"],
    ["Platform commission (15%)", money(d.platform_commission), "commission"],
    ["Business payout", money(d.business_payout), "payout"],
    ["Escrow status", status, "status"],
  ];
  if (d.released) rows.push(["Stripe transfer ID", d.transfer_id || "Not transferred yet (business bank payouts not enabled)", "transfer-id"]);
  return (
    <dl data-testid={`escrow-details-${d.job_id}`} className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v, t]) => (
        <div key={k} className="flex justify-between gap-3">
          <dt className="text-slate-500">{k}</dt>
          <dd data-testid={`escrow-${t}-${d.job_id}`} className="font-semibold text-slate-900 text-right break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function DisputeRow({ d, onChanged }) {
  const [open, setOpen] = useState(false);
  const refund = async () => {
    if (!window.confirm("Refund this payment and cancel the job?")) return;
    try { await api.post(`/admin/jobs/${d.job_id}/refund`); toast.success("Refunded"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className="flex items-center justify-between gap-3">
      <button type="button" data-testid={`escrow-toggle-${d.job_id}`} onClick={() => setOpen((o) => !o)} aria-expanded={open} className="text-left flex-1 flex items-start gap-2">
        <ChevronDown size={18} className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        <div>
        <h3 className="font-semibold text-slate-900">{d.job_title || d.job_id}</h3>
        <p className="text-sm text-slate-500">{d.business_name} · {d.job_status} · escrow {money(d.amount)} · {d.released ? "released" : "held"}{d.status === "refunded" ? " · refunded" : ""}</p>
        </div>
      </button>
      {!d.released && d.status !== "refunded" && (
        <Button data-testid={`admin-refund-${d.job_id}`} onClick={refund} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">Refund</Button>
      )}
    </div>
    {open && <EscrowDetails d={d} />}
    </div>
  );
}
